import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, ArrowLeft, Users, Gift, MapPin, Sparkles, Share2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public referral leaderboard. Route: /leaderboard/carriers
 *
 * Shows the top N carriers by referrals for the selected month. Only
 * displays obfuscated names ("First L." + state) — never emails or full
 * names — so the page is safe to link from social channels.
 */
export default function LeaderboardCarriersPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentMonthISO());

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE}/api/leaderboard/carriers`, { params: { month, limit: 25 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    document.title = `Carrier Leaderboard — Small carriers helping small carriers`;
    const desc = document.querySelector('meta[name="description"]');
    const orig = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content", "Public leaderboard of small carriers who referred the most fleets to FreightPilotAI this month. Free tools, community-first.");
    return () => { if (desc && orig != null) desc.setAttribute("content", orig); };
  }, []);

  const monthLabel = new Date(month + "-01T12:00:00Z").toLocaleDateString(undefined, {
    year: "numeric", month: "long",
  });

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="leaderboard-carriers-page">
      {/* Nav */}
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="leaderboard-home-link">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <Link to="/tools" className="text-sm text-white/70 hover:text-white transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> All Free Tools
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 25% 30%, #FF5A1F 0%, transparent 45%), radial-gradient(circle at 80% 70%, #FF5A1F 0%, transparent 45%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <Trophy className="w-3.5 h-3.5" />
            Public Referral Leaderboard
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-4">
            Small carriers helping<br />
            <span className="text-[#FF5A1F]">small carriers</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Every carrier below invited another fleet to FreightPilotAI. Both sides earned <b className="text-emerald-300">30 free days</b>. This is a community, not a marketing funnel.
          </p>
        </div>
      </section>

      {/* Month toggle + totals */}
      <section className="max-w-4xl mx-auto px-6 pb-6" data-testid="leaderboard-controls">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-1">Showing</div>
            <div className="text-2xl font-black tracking-tight">{monthLabel}</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={month} onChange={(e) => setMonth(e.target.value)}
              data-testid="leaderboard-month-select"
              className="bg-neutral-900 border-2 border-white/10 hover:border-white/30 focus:border-[#FF5A1F] rounded-lg px-3 py-2 text-white text-sm outline-none transition-colors">
              {monthOptions().map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Totals strip */}
        {data && data.leaders.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="leaderboard-totals">
            <TotalCard icon={<Users className="w-4 h-4" />} label="Carriers on the board" value={data.totals.carriers} />
            <TotalCard icon={<Sparkles className="w-4 h-4" />} label="Referrals this month" value={data.totals.referrals} />
            <TotalCard icon={<Gift className="w-4 h-4" />} label="Free days awarded" value={data.totals.days_awarded} />
          </div>
        )}
      </section>

      {/* Leaderboard table */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        {loading ? (
          <div className="py-16 text-center text-white/40 text-sm">Loading leaderboard…</div>
        ) : !data || data.leaders.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="rounded-2xl border-2 border-white/10 bg-white/[0.03] overflow-hidden" data-testid="leaderboard-rows">
            {data.leaders.map((row) => (
              <LeaderRow key={row.rank} row={row} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 rounded-2xl border-2 border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.05] p-6 sm:p-8 text-center" data-testid="leaderboard-cta">
          <Trophy className="w-8 h-8 text-[#FF5A1F] mx-auto mb-3" />
          <div className="text-2xl sm:text-3xl font-black tracking-tight mb-2">
            Want on the board next month?
          </div>
          <p className="text-white/60 text-sm max-w-lg mx-auto mb-5">
            Every carrier who signs up to FreightPilotAI via your referral link — or your public <code className="text-[#FF5A1F]">/jobs/:slug</code> page — earns you 30 free days and a spot on this list.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/careers-builder"
              data-testid="leaderboard-cta-careers"
              className="inline-flex items-center justify-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-5 py-3 text-sm transition-colors">
              Build my free careers page
            </Link>
            <Link to="/app/refer-earn"
              data-testid="leaderboard-cta-refer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg px-5 py-3 text-sm transition-colors">
              <Share2 className="w-4 h-4" />
              Get my referral link
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─────────────────── row ─────────────────── */
function LeaderRow({ row }) {
  const isTopThree = row.rank <= 3;
  const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : null;
  return (
    <div
      className={`flex items-center gap-4 p-4 sm:p-5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors ${isTopThree ? "bg-[#FF5A1F]/[0.03]" : ""}`}
      data-testid={`leaderboard-row-${row.rank}`}
    >
      <div className="w-11 shrink-0 text-center">
        {medal ? (
          <div className="text-2xl">{medal}</div>
        ) : (
          <div className="text-lg font-black tracking-tight text-white/40">#{row.rank}</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white text-base leading-snug truncate">
          {row.display_name}
          {row.used_careers_page && (
            <span className="ml-2 inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 align-middle">
              via careers page
            </span>
          )}
        </div>
        {row.state && (
          <div className="text-[11px] text-white/40 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" />
            {row.state}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className={`text-2xl sm:text-3xl font-black tracking-tight ${isTopThree ? "text-[#FF5A1F]" : "text-white"}`}>
          {row.referrals}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
          {row.referrals === 1 ? "referral" : "referrals"}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block min-w-[70px]">
        <div className="text-sm font-bold text-emerald-300">{row.reward_days}d</div>
        <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold">Free days</div>
      </div>
    </div>
  );
}

function TotalCard({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center gap-2 text-white/50 mb-2">
        <div className="w-6 h-6 rounded bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          {icon}
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border-2 border-dashed border-white/10 py-14 text-center" data-testid="leaderboard-empty">
      <Trophy className="w-10 h-10 text-white/20 mx-auto mb-3" />
      <div className="text-white font-semibold mb-1">Nobody's on the board yet this month</div>
      <div className="text-sm text-white/50 max-w-md mx-auto leading-relaxed mb-4">
        The first carrier to invite another fleet gets 🥇 for the whole month. Grab your referral link and share it.
      </div>
      <Link to="/app/refer-earn" className="inline-flex items-center gap-1.5 text-[#FF5A1F] hover:text-[#FF7038] font-semibold text-sm">
        <Share2 className="w-4 h-4" />
        Get my referral link →
      </Link>
    </div>
  );
}

/* ─────────────────── helpers ─────────────────── */
function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthOptions() {
  const opts = [];
  const d = new Date();
  for (let i = 0; i < 6; i += 1) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const value = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
    const label = m.toLocaleDateString(undefined, { year: "numeric", month: "long" });
    opts.push({ value, label: i === 0 ? `${label} (this month)` : label });
  }
  return opts;
}
